import { CommonModule } from "@angular/common";
import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router } from "@angular/router";
import { QueueTabEnum } from "../../../dashboard/models/queue-model";
import { ExpenseType, TripAddressDto, TripRequestType } from "../../../dashboard/models/trip-request-models";
import { TripRequestService } from "../../../dashboard/services/trip-request.service";
import { TripRequestFormFactory } from "../../shared/trip-request-form.factory";
import { TripRequestFormMapper } from "../../shared/trip-request-form.mapper";
import { TripRequestFormService } from "../../shared/trip-request-form.service";
import { ConsumerDetailsComponent } from "../consumer-details/consumer-details.component";
import { TripDetailsComponent } from "../trip-details/trip-details.component";
import { AgentTripsummaryComponent } from "../trip-summary/agent-trip-summary.component";

@Component({
  selector: 'agent-trip-request',
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatDialogModule,
    ReactiveFormsModule,
    ConsumerDetailsComponent,
    TripDetailsComponent,
    AgentTripsummaryComponent
],
  templateUrl: './agent-trip-request.component.html',
  styleUrl: './agent-trip-request.component.scss',
})

export class AgentTripRequestComponent implements OnInit {
  private tripRequestFormFactory = inject(TripRequestFormFactory);
  private tripRequestFormService = inject(TripRequestFormService);
  private tripRequestService = inject(TripRequestService);
  private tripRequestFormMapper = inject(TripRequestFormMapper);
  @ViewChild(TripDetailsComponent) tripDetailsComponent!: TripDetailsComponent;
  @ViewChild('confirmationDialogTemplate', { static: true }) confirmationDialogTemplate!: TemplateRef<any>;
  agentTripRequestForm!: FormGroup;
  confirmationNo = '';
  private el = inject(ElementRef);
  private dialogRef?: MatDialogRef<any>;
  private dialog = inject(MatDialog);
  private router = inject(Router);
  isCofirmationMessage = true;
  dialogTitle = 'Trip Confirmation';
  dialogContent = '';
  enteredRecipientId = 0;


  get tripRequest() : FormGroup {
    return this.agentTripRequestForm.get('tripRequest') as FormGroup;
  }

  get tripAppointments() : FormArray {
    return this.tripRequest.get('tripAppointments') as FormArray;
  }

  get tripAddresses() : FormArray {
    return this.tripRequest.get('tripAddresses') as FormArray;
  }

  ngOnInit(): void {
    this.agentTripRequestForm = this.tripRequestFormFactory.createAgentTripRequestForm();
  }

  goToConsumerDetails(stepper : MatStepper) {
      stepper.selectedIndex = 0;
  }

  goToTripEntry(stepper : MatStepper, direction :string) {
     if (direction !== "Back") {
        const recipientIdControl = this.tripRequest.get('recipientId') as FormControl;
        recipientIdControl?.markAsTouched();

        if (recipientIdControl?.invalid || isNaN(recipientIdControl.value) ) {
          return;
        }

        if (Number(recipientIdControl?.value) !== this.enteredRecipientId)
        {
          this.enteredRecipientId = recipientIdControl?.value;
          this.tripDetailsComponent.intializeTripEntryForm();
          stepper.selectedIndex = 1;
        }
        else {
            stepper.selectedIndex = 1;
        }
    }
    else {
      stepper.selectedIndex = 1;
    }
  }

  goToSummary(stepper : MatStepper) {
     this.tripRequest.markAsUntouched();
    if (this.validateVisibleTripRequestControls(this.tripRequest)) {
        const tripAppointments = this.tripRequest.get('tripAppointments') as FormArray;
        const tripAddresses = this.tripRequest.get('tripAddresses') as FormArray;
        const addressDto = tripAddresses?.value as TripAddressDto[];
        this.tripRequestService.getTripLegs(addressDto, this.tripRequest.get('isRoundTrip')?.value)
        .subscribe((data) => {
              if(data) {
                  this.tripRequestFormService.loadAgentTripLegs(tripAppointments, data, this.tripRequest.get('tripType')?.value);
                  stepper.selectedIndex = 2;
              }
              else {
                this.dialogTitle = 'Address Error';
                this.dialogContent = 'The address entered cannot be verified. Please review the entered information and correct the details, if needed.'
                this.isCofirmationMessage = false;
                this.openDialog(true);
              }
        });  
    }
    else {
      this.tripRequest.markAllAsTouched();
    }
  }

  saveTripRequest() {
    const tripRequestType = this.tripRequest.get('tripType')?.value === 'Post' ? TripRequestType.Post : TripRequestType.Pre;
    if (this.tripRequestFormService.validateTripSummary(this.tripRequest, tripRequestType)) {
        const formData = this.getFormData();
        this.tripRequestService.saveAgentTripRequest(formData).subscribe(data => {
          if (data == 'Invalid Eligibility') {
                this.dialogTitle = 'Unverified Eligibility';
                this.dialogContent = 'We are unable to verify your eligibility at this time, please contact your local Medical Assistance transportation office for any questions.'
                this.isCofirmationMessage = false;
                this.openDialog(false);
          }
          else if (this.tripRequest.get('isSameAgentWillReview')?.value) {
            this.router.navigate(['/dashboard/trip-request-review'], {
                  state: {
                    requestId: Number(data),
                    queueType: QueueTabEnum.MY_QUEUE,
                    isAgentTripRequest: true
                  },
                });
          }
          else {
              this.confirmationNo = data;
              this.dialogTitle = 'Request Confirmation';
              this.dialogContent = 'The trip has been successfully submitted. Your confirmation number is ' + this.confirmationNo;
              this.isCofirmationMessage = true
              this.openDialog(true);
          }
        })
      }
  }

  openDialog(disableClose: boolean) {
    const rect = this.el.nativeElement.getBoundingClientRect();
    const config = new MatDialogConfig();
    config.width = '40%';
    config.disableClose= disableClose;
    this.dialogRef = this.dialog.open(this.confirmationDialogTemplate, config);
  }

  closeDialog() {
    if (this.isCofirmationMessage) { this.router.navigate(['/dashboard']); }
    this.dialogRef?.close();
  }

  private validateVisibleTripRequestControls(tripRequestFormGroup: FormGroup): boolean {
    const isPostTrip = this.tripRequest.get('tripType')?.value === 'Post';

    if (!this.tripRequestFormService.validateAppointmentsData(this.tripAppointments)) {
      return false;
    }

    if (isPostTrip && !this.tripRequestFormService.validateDriverInformation(tripRequestFormGroup)) {
      return false;
    }

    if (isPostTrip && !this.tripRequestFormService.validatePassenger(tripRequestFormGroup)) {
      return false;
    }

    return this.tripRequestFormService.validateTripLocation(tripRequestFormGroup);
  }

  getFormData(): FormData {
    const formData = this.tripRequestFormMapper.getTripRequestFormData(this.tripRequest);
    const verificationDocs = this.tripDetailsComponent.appVerficationDocumentsDto;
    const tollDocs = this.tripDetailsComponent.tollDocumentsDto;
    const parkingDocs = this.tripDetailsComponent.parkingDocumentsDto;

    var index = this.tripRequestFormMapper.appendDocumentsData(formData,verificationDocs,'Verification', 0);
    index = this.tripRequestFormMapper.appendDocumentsData(formData,tollDocs,ExpenseType.Toll, index);
    index = this.tripRequestFormMapper.appendDocumentsData(formData,parkingDocs,ExpenseType.Toll, index);

    return formData;
  }

}


